import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminHeader from "@/components/admin/AdminHeader";
import BatchPlanner from "@/components/admin/BatchPlanner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Edit2, Trash2, Eye, FileText, CheckCircle2, Clock } from "lucide-react";

export default function AdminDashboard() {
  const { authFetch } = useAuth();
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("all"); // all | draft | published
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/admin/articles");
      const data = await res.json();
      setArticles(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet article ?")) return;
    await authFetch(`/admin/articles/${id}`, { method: "DELETE" });
    load();
  };

  const togglePublish = async (a) => {
    const newStatus = a.status === "published" ? "draft" : "published";
    await authFetch(`/admin/articles/${a.id}`, { method: "PUT", body: JSON.stringify({ status: newStatus }) });
    load();
  };

  const filtered = filter === "all" ? articles : articles.filter((a) => a.status === filter);
  const counts = {
    all: articles.length,
    published: articles.filter((a) => a.status === "published").length,
    scheduled: articles.filter((a) => a.status === "scheduled").length,
    draft: articles.filter((a) => a.status === "draft").length,
  };

  return (
    <div className="min-h-screen bg-md-bg">
      <AdminHeader />

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl text-md-primary font-medium">Vos articles</h1>
            <p className="text-md-muted mt-2">Rédigez, générez via IA, publiez ou archivez en quelques clics.</p>
          </div>
          <div className="flex flex-wrap gap-3 self-start">
          <BatchPlanner onDone={load} />
          <Link
            to="/admin/articles/new"
            data-testid="admin-new-article-btn"
            className="inline-flex items-center gap-2 bg-md-secondary hover:bg-md-secondaryHover text-white px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-md-secondary/30 self-start"
          >
            <Plus className="w-4 h-4" /> Nouvel article
          </Link>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-filter-tabs">
          {[
            { key: "all", label: "Tous" },
            { key: "published", label: "Publiés" },
            { key: "scheduled", label: "Programmés" },
            { key: "draft", label: "Brouillons" },
          ].map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === f.key
                  ? "bg-md-primary text-white"
                  : "bg-white text-md-muted hover:text-md-primary border border-md-border"
              }`}
            >
              {f.label} <span className="opacity-60">({counts[f.key]})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-md-muted">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty-state" className="text-center py-20 bg-white rounded-3xl border border-md-border">
            <FileText className="w-12 h-12 mx-auto text-md-muted/40 mb-4" />
            <p className="text-md-muted mb-6">Aucun article {filter !== "all" ? `(${filter})` : ""} pour l'instant.</p>
            <Link to="/admin/articles/new" className="inline-flex items-center gap-2 bg-md-primary text-white px-5 py-2.5 rounded-full text-sm font-semibold">
              <Plus className="w-4 h-4" /> Écrire le premier article
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-articles-list">
            {filtered.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-article-${a.id}`}
                className="bg-white border border-md-border rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4 hover:border-md-secondary/30 transition-colors"
              >
                <img src={a.image} alt="" className="w-full md:w-24 h-24 object-cover rounded-xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-md-secondary">{a.category}</span>
                    {a.status === "published" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Publié
                      </span>
                    ) : a.status === "scheduled" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        <Clock className="w-3 h-3" /> Programmé {a.scheduled_at ? `· ${new Date(a.scheduled_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        <Clock className="w-3 h-3" /> Brouillon
                      </span>
                    )}
                    <span className="text-xs text-md-muted">{a.read_time}</span>
                  </div>
                  <h3 className="font-serif text-lg text-md-primary font-medium truncate">{a.title}</h3>
                  <p className="text-xs text-md-muted mt-1 line-clamp-1">{a.excerpt}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {a.status === "published" && (
                    <a
                      href={`/article/${a.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Voir en ligne"
                      className="w-9 h-9 rounded-full grid place-items-center bg-md-bg hover:bg-md-primary hover:text-white text-md-muted transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    data-testid={`toggle-publish-${a.id}`}
                    onClick={() => togglePublish(a)}
                    title={a.status === "published" ? "Dépublier" : "Publier"}
                    className={`px-3 h-9 rounded-full text-xs font-semibold transition-all ${
                      a.status === "published"
                        ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    }`}
                  >
                    {a.status === "published" ? "Dépublier" : "Publier"}
                  </button>
                  <Link
                    to={`/admin/articles/${a.id}`}
                    className="w-9 h-9 rounded-full grid place-items-center bg-md-primary text-white hover:bg-md-primaryHover"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Link>
                  <button
                    data-testid={`delete-${a.id}`}
                    onClick={() => remove(a.id)}
                    title="Supprimer"
                    className="w-9 h-9 rounded-full grid place-items-center bg-red-50 text-red-600 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
